import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Resume.css';
import axios from 'axios';
import { useAuth } from './context/AuthContext';
import {
  Save,
  Eye,
  User,
  Briefcase,
  GraduationCap,
  Award,
  Code,
  Globe,
  Mail,
  Phone,
  MapPin,
  Github,
  Linkedin,
  Plus,
  Trash2,
  FileText,
  Sparkles
} from 'lucide-react';

function Resume() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const userid = user?.userid;

  const [resumeData, setResumeData] = useState({
    personalInfo: {
      name: '',
      headline: '',
      location: '',
      email: '',
      phone: '',
      website: '',
      linkedin: '',
      github: '',
    },
    summary: '',
    experience: [],
    education: [],
    skills: [],
    projects: [],
    certifications: [],
    languages: [],
  });

  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');

  useEffect(() => {
    if (!userid) return;

    const savedResume = localStorage.getItem(`resume_${userid}`);
    if (savedResume) {
      setResumeData(JSON.parse(savedResume));
    }
  }, [userid]);

  const saveResume = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Please login again");
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/resume/save`,
        resumeData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        localStorage.setItem(`resume_${userid}`, JSON.stringify(resumeData));
        alert("Resume saved successfully");
      }
    } catch (err) {
      console.error("Resume save error:", err.response?.data || err.message);
      alert("Failed to save resume");
    } finally {
      setSaving(false);
    }
  };

  const handlePersonalInfoChange = (field, value) => {
    setResumeData(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        [field]: value
      }
    }));
  };

  const handleArrayItemChange = (section, index, field, value) => {
    setResumeData(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addArrayItem = (section, template) => {
    setResumeData(prev => ({
      ...prev,
      [section]: [...prev[section], { ...template, id: Date.now() + Math.random() }]
    }));
  };

  const removeArrayItem = (section, index) => {
    setResumeData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const handleSkillChange = (index, value) => {
    handleArrayItemChange('skills', index, 'name', value);
  };

  const addSkill = () => {
    addArrayItem('skills', { name: '', category: 'Technical' });
  };

  const removeSkill = (index) => {
    removeArrayItem('skills', index);
  };

  const viewProfile = () => {
    const profileData = {
      id: userid,
      name: resumeData.personalInfo.name,
      headline: resumeData.personalInfo.headline,
      location: resumeData.personalInfo.location,
      connections: 0,
      followers: 0,
      profileViews: 0,
      about: resumeData.summary,
      contactInfo: {
        email: resumeData.personalInfo.email,
        website: resumeData.personalInfo.website,
        phone: resumeData.personalInfo.phone
      },
      experience: resumeData.experience,
      education: resumeData.education,
      skills: resumeData.skills,
      recommendations: [],
      achievements: resumeData.certifications
    };
    
    if (!userid) return;
    navigate(`/profile/${userid}`);
  };

  const sections = [
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'summary', label: 'Summary', icon: FileText },
    { id: 'experience', label: 'Experience', icon: Briefcase },
    { id: 'education', label: 'Education', icon: GraduationCap },
    { id: 'skills', label: 'Skills', icon: Code },
    { id: 'projects', label: 'Projects', icon: Sparkles },
    { id: 'certifications', label: 'Certifications', icon: Award }
  ];

  return (
    <div className="resume-container">
      {/* Header */}
      <div className="resume-header">
        <div className="resume-header-left">
          <h1>Resume Builder</h1>
          <p>Create your professional resume</p>
        </div>
        <div className="resume-actions">
          <button className="btn-view" onClick={viewProfile}>
            <Eye size={18} />
            <span>Preview</span>
          </button>
          <button className="btn-save" onClick={saveResume} disabled={saving}>
            {saving ? (
              <>
                <div className="spinner-small" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={18} />
                <span>Save</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Section Navigation */}
      <div className="resume-mobile-nav">
        <select 
          value={activeSection} 
          onChange={(e) => setActiveSection(e.target.value)}
          className="mobile-section-select"
        >
          {sections.map(section => (
            <option key={section.id} value={section.id}>
              {section.label}
            </option>
          ))}
        </select>
      </div>

      {/* Desktop Section Tabs */}
      <div className="resume-tabs">
        {sections.map(section => (
          <button
            key={section.id}
            className={`resume-tab ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            <section.icon size={16} />
            <span>{section.label}</span>
          </button>
        ))}
      </div>

      <div className="resume-sections">
        {/* Personal Information */}
        {activeSection === 'personal' && (
          <ResumeSection title="Personal Information" icon={User}>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name *</label>
                <div className="input-wrapper">
                  <User size={16} className="input-icon" />
                  <input
                    type="text"
                    value={resumeData.personalInfo.name}
                    onChange={(e) => handlePersonalInfoChange('name', e.target.value)}
                    placeholder="John Doe"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Professional Headline</label>
                <div className="input-wrapper">
                  <Briefcase size={16} className="input-icon" />
                  <input
                    type="text"
                    value={resumeData.personalInfo.headline}
                    onChange={(e) => handlePersonalInfoChange('headline', e.target.value)}
                    placeholder="Frontend Developer"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Location</label>
                <div className="input-wrapper">
                  <MapPin size={16} className="input-icon" />
                  <input
                    type="text"
                    value={resumeData.personalInfo.location}
                    onChange={(e) => handlePersonalInfoChange('location', e.target.value)}
                    placeholder="San Francisco, CA"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Email *</label>
                <div className="input-wrapper">
                  <Mail size={16} className="input-icon" />
                  <input
                    type="email"
                    value={resumeData.personalInfo.email}
                    onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Phone</label>
                <div className="input-wrapper">
                  <Phone size={16} className="input-icon" />
                  <input
                    type="tel"
                    value={resumeData.personalInfo.phone}
                    onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                    placeholder="(123) 456-7890"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Website</label>
                <div className="input-wrapper">
                  <Globe size={16} className="input-icon" />
                  <input
                    type="url"
                    value={resumeData.personalInfo.website}
                    onChange={(e) => handlePersonalInfoChange('website', e.target.value)}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>LinkedIn</label>
                <div className="input-wrapper">
                  <Linkedin size={16} className="input-icon" />
                  <input
                    type="url"
                    value={resumeData.personalInfo.linkedin}
                    onChange={(e) => handlePersonalInfoChange('linkedin', e.target.value)}
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>GitHub</label>
                <div className="input-wrapper">
                  <Github size={16} className="input-icon" />
                  <input
                    type="url"
                    value={resumeData.personalInfo.github}
                    onChange={(e) => handlePersonalInfoChange('github', e.target.value)}
                    placeholder="https://github.com/username"
                  />
                </div>
              </div>
            </div>
          </ResumeSection>
        )}

        {/* Professional Summary */}
        {activeSection === 'summary' && (
          <ResumeSection title="Professional Summary" icon={FileText}>
            <div className="form-group">
              <textarea
                value={resumeData.summary}
                onChange={(e) => setResumeData(prev => ({ ...prev, summary: e.target.value }))}
                placeholder="Write a brief summary of your professional background, skills, and career goals..."
                rows="6"
              />
              <p className="field-hint">Briefly describe your professional background and key strengths</p>
            </div>
          </ResumeSection>
        )}

        {/* Work Experience */}
        {activeSection === 'experience' && (
          <ResumeSection 
            title="Work Experience" 
            icon={Briefcase}
            onAdd={() => addArrayItem('experience', {
              title: '',
              company: '',
              location: '',
              startDate: '',
              endDate: '',
              current: false,
              description: '',
              skills: []
            })}
          >
            {resumeData.experience.length === 0 ? (
              <div className="empty-state">
                <Briefcase size={48} />
                <h4>No experience added</h4>
                <p>Add your work experience to showcase your professional journey</p>
                <button 
                  className="btn-add-empty"
                  onClick={() => addArrayItem('experience', {
                    title: '',
                    company: '',
                    location: '',
                    startDate: '',
                    endDate: '',
                    current: false,
                    description: '',
                    skills: []
                  })}
                >
                  <Plus size={16} />
                  Add Experience
                </button>
              </div>
            ) : (
              resumeData.experience.map((exp, index) => (
                <div key={exp.id || index} className="section-item">
                  <div className="item-header">
                    <h4>
                      <Briefcase size={16} />
                      {exp.title || 'New Position'}
                    </h4>
                    <button 
                      className="btn-remove"
                      onClick={() => removeArrayItem('experience', index)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Job Title *</label>
                      <input
                        type="text"
                        value={exp.title}
                        onChange={(e) => handleArrayItemChange('experience', index, 'title', e.target.value)}
                        placeholder="Senior Frontend Developer"
                      />
                    </div>
                    <div className="form-group">
                      <label>Company *</label>
                      <input
                        type="text"
                        value={exp.company}
                        onChange={(e) => handleArrayItemChange('experience', index, 'company', e.target.value)}
                        placeholder="TechCorp"
                      />
                    </div>
                    <div className="form-group">
                      <label>Location</label>
                      <input
                        type="text"
                        value={exp.location}
                        onChange={(e) => handleArrayItemChange('experience', index, 'location', e.target.value)}
                        placeholder="San Francisco, CA"
                      />
                    </div>
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="text"
                        value={exp.startDate}
                        onChange={(e) => handleArrayItemChange('experience', index, 'startDate', e.target.value)}
                        placeholder="Mar 2021"
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input
                        type="text"
                        value={exp.endDate}
                        onChange={(e) => handleArrayItemChange('experience', index, 'endDate', e.target.value)}
                        placeholder="Present"
                        disabled={exp.current}
                      />
                    </div>
                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={exp.current}
                          onChange={(e) => handleArrayItemChange('experience', index, 'current', e.target.checked)}
                        />
                        <span>Currently working here</span>
                      </label>
                    </div>
                    <div className="form-group full-width">
                      <label>Description</label>
                      <textarea
                        value={exp.description}
                        onChange={(e) => handleArrayItemChange('experience', index, 'description', e.target.value)}
                        placeholder="Describe your responsibilities and achievements..."
                        rows="4"
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
            {resumeData.experience.length > 0 && (
              <button 
                className="btn-add-item"
                onClick={() => addArrayItem('experience', {
                  title: '',
                  company: '',
                  location: '',
                  startDate: '',
                  endDate: '',
                  current: false,
                  description: '',
                  skills: []
                })}
              >
                <Plus size={16} />
                Add Another Experience
              </button>
            )}
          </ResumeSection>
        )}

        {/* Education */}
        {activeSection === 'education' && (
          <ResumeSection 
            title="Education" 
            icon={GraduationCap}
            onAdd={() => addArrayItem('education', {
              school: '',
              degree: '',
              field: '',
              startDate: '',
              endDate: '',
              gpa: '',
              description: ''
            })}
          >
            {resumeData.education.length === 0 ? (
              <div className="empty-state">
                <GraduationCap size={48} />
                <h4>No education added</h4>
                <p>Add your educational background</p>
                <button 
                  className="btn-add-empty"
                  onClick={() => addArrayItem('education', {
                    school: '',
                    degree: '',
                    field: '',
                    startDate: '',
                    endDate: '',
                    gpa: '',
                    description: ''
                  })}
                >
                  <Plus size={16} />
                  Add Education
                </button>
              </div>
            ) : (
              resumeData.education.map((edu, index) => (
                <div key={edu.id || index} className="section-item">
                  <div className="item-header">
                    <h4>
                      <GraduationCap size={16} />
                      {edu.school || 'New Education'}
                    </h4>
                    <button 
                      className="btn-remove"
                      onClick={() => removeArrayItem('education', index)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>School/University *</label>
                      <input
                        type="text"
                        value={edu.school}
                        onChange={(e) => handleArrayItemChange('education', index, 'school', e.target.value)}
                        placeholder="Stanford University"
                      />
                    </div>
                    <div className="form-group">
                      <label>Degree *</label>
                      <input
                        type="text"
                        value={edu.degree}
                        onChange={(e) => handleArrayItemChange('education', index, 'degree', e.target.value)}
                        placeholder="Bachelor of Science"
                      />
                    </div>
                    <div className="form-group">
                      <label>Field of Study</label>
                      <input
                        type="text"
                        value={edu.field}
                        onChange={(e) => handleArrayItemChange('education', index, 'field', e.target.value)}
                        placeholder="Computer Science"
                      />
                    </div>
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="text"
                        value={edu.startDate}
                        onChange={(e) => handleArrayItemChange('education', index, 'startDate', e.target.value)}
                        placeholder="2017"
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input
                        type="text"
                        value={edu.endDate}
                        onChange={(e) => handleArrayItemChange('education', index, 'endDate', e.target.value)}
                        placeholder="2021"
                      />
                    </div>
                    <div className="form-group">
                      <label>GPA</label>
                      <input
                        type="text"
                        value={edu.gpa}
                        onChange={(e) => handleArrayItemChange('education', index, 'gpa', e.target.value)}
                        placeholder="3.8/4.0"
                      />
                    </div>
                    <div className="form-group full-width">
                      <label>Description</label>
                      <textarea
                        value={edu.description}
                        onChange={(e) => handleArrayItemChange('education', index, 'description', e.target.value)}
                        placeholder="Add details about your studies, achievements, or activities..."
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
            {resumeData.education.length > 0 && (
              <button 
                className="btn-add-item"
                onClick={() => addArrayItem('education', {
                  school: '',
                  degree: '',
                  field: '',
                  startDate: '',
                  endDate: '',
                  gpa: '',
                  description: ''
                })}
              >
                <Plus size={16} />
                Add Another Education
              </button>
            )}
          </ResumeSection>
        )}

        {/* Skills */}
        {activeSection === 'skills' && (
          <ResumeSection 
            title="Skills" 
            icon={Code}
            onAdd={addSkill}
          >
            {resumeData.skills.length === 0 ? (
              <div className="empty-state">
                <Code size={48} />
                <h4>No skills added</h4>
                <p>Add your professional skills</p>
                <button className="btn-add-empty" onClick={addSkill}>
                  <Plus size={16} />
                  Add Skill
                </button>
              </div>
            ) : (
              <div className="skills-container">
                {resumeData.skills.map((skill, index) => (
                  <div key={index} className="skill-input-row">
                    <input
                      type="text"
                      value={skill.name}
                      onChange={(e) => handleSkillChange(index, e.target.value)}
                      placeholder="e.g., React, JavaScript, Python"
                      className="skill-input"
                    />
                    <button 
                      className="btn-remove-small"
                      onClick={() => removeSkill(index)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button className="btn-add-item" onClick={addSkill}>
                  <Plus size={16} />
                  Add Another Skill
                </button>
              </div>
            )}
          </ResumeSection>
        )}

        {/* Projects */}
        {activeSection === 'projects' && (
          <ResumeSection 
            title="Projects" 
            icon={Sparkles}
            onAdd={() => addArrayItem('projects', {
              name: '',
              description: '',
              technologies: '',
              link: '',
              date: ''
            })}
          >
            {resumeData.projects.length === 0 ? (
              <div className="empty-state">
                <Sparkles size={48} />
                <h4>No projects added</h4>
                <p>Showcase your personal and professional projects</p>
                <button 
                  className="btn-add-empty"
                  onClick={() => addArrayItem('projects', {
                    name: '',
                    description: '',
                    technologies: '',
                    link: '',
                    date: ''
                  })}
                >
                  <Plus size={16} />
                  Add Project
                </button>
              </div>
            ) : (
              resumeData.projects.map((project, index) => (
                <div key={project.id || index} className="section-item">
                  <div className="item-header">
                    <h4>
                      <Sparkles size={16} />
                      {project.name || 'New Project'}
                    </h4>
                    <button 
                      className="btn-remove"
                      onClick={() => removeArrayItem('projects', index)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Project Name *</label>
                      <input
                        type="text"
                        value={project.name}
                        onChange={(e) => handleArrayItemChange('projects', index, 'name', e.target.value)}
                        placeholder="E-commerce Platform"
                      />
                    </div>
                    <div className="form-group">
                      <label>Technologies</label>
                      <input
                        type="text"
                        value={project.technologies}
                        onChange={(e) => handleArrayItemChange('projects', index, 'technologies', e.target.value)}
                        placeholder="React, Node.js, MongoDB"
                      />
                    </div>
                    <div className="form-group">
                      <label>Project Link</label>
                      <input
                        type="url"
                        value={project.link}
                        onChange={(e) => handleArrayItemChange('projects', index, 'link', e.target.value)}
                        placeholder="https://github.com/username/project"
                      />
                    </div>
                    <div className="form-group">
                      <label>Date</label>
                      <input
                        type="text"
                        value={project.date}
                        onChange={(e) => handleArrayItemChange('projects', index, 'date', e.target.value)}
                        placeholder="Jan 2023"
                      />
                    </div>
                    <div className="form-group full-width">
                      <label>Description</label>
                      <textarea
                        value={project.description}
                        onChange={(e) => handleArrayItemChange('projects', index, 'description', e.target.value)}
                        placeholder="Describe the project and your role..."
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
            {resumeData.projects.length > 0 && (
              <button 
                className="btn-add-item"
                onClick={() => addArrayItem('projects', {
                  name: '',
                  description: '',
                  technologies: '',
                  link: '',
                  date: ''
                })}
              >
                <Plus size={16} />
                Add Another Project
              </button>
            )}
          </ResumeSection>
        )}

        {/* Certifications */}
        {activeSection === 'certifications' && (
          <ResumeSection 
            title="Certifications" 
            icon={Award}
            onAdd={() => addArrayItem('certifications', {
              name: '',
              issuer: '',
              date: '',
              credentialId: ''
            })}
          >
            {resumeData.certifications.length === 0 ? (
              <div className="empty-state">
                <Award size={48} />
                <h4>No certifications added</h4>
                <p>Add your professional certifications</p>
                <button 
                  className="btn-add-empty"
                  onClick={() => addArrayItem('certifications', {
                    name: '',
                    issuer: '',
                    date: '',
                    credentialId: ''
                  })}
                >
                  <Plus size={16} />
                  Add Certification
                </button>
              </div>
            ) : (
              resumeData.certifications.map((cert, index) => (
                <div key={cert.id || index} className="section-item">
                  <div className="item-header">
                    <h4>
                      <Award size={16} />
                      {cert.name || 'New Certification'}
                    </h4>
                    <button 
                      className="btn-remove"
                      onClick={() => removeArrayItem('certifications', index)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Certification Name *</label>
                      <input
                        type="text"
                        value={cert.name}
                        onChange={(e) => handleArrayItemChange('certifications', index, 'name', e.target.value)}
                        placeholder="AWS Certified Solutions Architect"
                      />
                    </div>
                    <div className="form-group">
                      <label>Issuing Organization</label>
                      <input
                        type="text"
                        value={cert.issuer}
                        onChange={(e) => handleArrayItemChange('certifications', index, 'issuer', e.target.value)}
                        placeholder="Amazon Web Services"
                      />
                    </div>
                    <div className="form-group">
                      <label>Date Obtained</label>
                      <input
                        type="text"
                        value={cert.date}
                        onChange={(e) => handleArrayItemChange('certifications', index, 'date', e.target.value)}
                        placeholder="Jan 2023"
                      />
                    </div>
                    <div className="form-group">
                      <label>Credential ID</label>
                      <input
                        type="text"
                        value={cert.credentialId}
                        onChange={(e) => handleArrayItemChange('certifications', index, 'credentialId', e.target.value)}
                        placeholder="ABC123XYZ"
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
            {resumeData.certifications.length > 0 && (
              <button 
                className="btn-add-item"
                onClick={() => addArrayItem('certifications', {
                  name: '',
                  issuer: '',
                  date: '',
                  credentialId: ''
                })}
              >
                <Plus size={16} />
                Add Another Certification
              </button>
            )}
          </ResumeSection>
        )}
      </div>
    </div>
  );
}

function ResumeSection({ title, icon: Icon, children, onAdd }) {
  return (
    <div className="resume-section">
      <div className="section-header">
        <div className="section-title">
          {Icon && <Icon size={20} />}
          <h3>{title}</h3>
        </div>
        {onAdd && (
          <button className="btn-add" onClick={onAdd}>
            <Plus size={16} />
            <span>Add</span>
          </button>
        )}
      </div>
      <div className="section-content">
        {children}
      </div>
    </div>
  );
}

export default Resume;